import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { SectorLexiconRow } from '../lib/types';
import { formatScore } from '../lib/format';
import styles from './SectorLexiconAccordion.module.css';

export function SectorLexiconAccordion({ sectors, className }: { sectors: SectorLexiconRow[]; className?: string }) {
  const [openId, setOpenId] = useState<string | null>(null);

  return (
    <ul className={[styles.list, className].filter(Boolean).join(' ')}>
      {sectors.map((s) => {
        const open = openId === s.name;
        const tone =
          s.score >= 0.25 ? 'var(--tia-positive)' : s.score <= -0.25 ? 'var(--tia-negative)' : 'var(--tia-warning)';
        return (
          <li key={s.name} className={styles.item}>
            <button
              type="button"
              className={styles.row}
              onClick={() => setOpenId(open ? null : s.name)}
              aria-expanded={open}
            >
              <span className={styles.name}>{s.name}</span>
              <span className={styles.matches}>{s.matches} matches</span>
              <span className={styles.counters}>
                <span className={[styles.counter, styles.pos].join(' ')}>{s.pos}</span>
                <span className={[styles.counter, styles.neg].join(' ')}>{s.neg}</span>
                <span className={[styles.counter, styles.risk].join(' ')}>{s.risk}</span>
              </span>
              <span className={[styles.score, 'mono'].join(' ')} style={{ color: tone }}>
                {formatScore(s.score)}
              </span>
              <ChevronDown
                size={16}
                strokeWidth={1.5}
                className={[styles.chev, open ? styles.chevOpen : ''].join(' ')}
                aria-hidden="true"
              />
            </button>
            {open && s.terms && (
              <div className={styles.detail}>
                {s.terms.positive && s.terms.positive.length > 0 && (
                  <TermGroup label="Positive" tone="pos" terms={s.terms.positive} />
                )}
                {s.terms.negative && s.terms.negative.length > 0 && (
                  <TermGroup label="Negative" tone="neg" terms={s.terms.negative} />
                )}
                {s.terms.risk && s.terms.risk.length > 0 && (
                  <TermGroup label="Risk" tone="risk" terms={s.terms.risk} />
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function TermGroup({ label, tone, terms }: { label: string; tone: 'pos' | 'neg' | 'risk'; terms: string[] }) {
  return (
    <div className={styles.termGroup}>
      <span className={styles.termGroupLabel}>{label}</span>
      <ul className={styles.termList}>
        {terms.map((t) => (
          <li key={t} className={[styles.term, styles[tone]].join(' ')}>{t}</li>
        ))}
      </ul>
    </div>
  );
}
